/**
 * PartnerForm
 *
 * Форма ввода данных партнёра для расчёта совместимости.
 * Использует каскадные селекторы регионов России.
 */

import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  RUSSIA_REGIONS,
  getSubjects,
  getDistricts,
  getSettlements,
  type Settlement
} from '../../shared/data/russiaRegions';

// ============================================================
// Типы
// ============================================================

interface PartnerFormData {
  name: string;
  relation: string;
  birthDate: string;
  birthTime: string;
  federalDistrict: string;
  subject: string;
  district: string;
  settlement: string;
  latitude: string;
  longitude: string;
}

interface PartnerFormResult {
  name: string;
  relation: string;
  birthDate: string;
  birthTime?: string;
  birthPlace: string;
  latitude: number;
  longitude: number;
  timezone?: string;
}

interface PartnerFormProps {
  onSubmit: (data: PartnerFormResult) => void;
  onCancel: () => void;
}

// ============================================================
// Стили
// ============================================================

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  padding: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  position: 'relative',
};

const btnPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '12px 28px',
  borderRadius: '12px',
  color: '#0A0814',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const btnSecondaryStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  color: '#b0b0b0',
  padding: '10px 24px',
  borderRadius: '12px',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'rgba(13, 20, 40, 0.6)',
  border: '1px solid rgba(212, 175, 55, 0.15)',
  borderRadius: '12px',
  color: '#fff',
  fontSize: '15px',
  boxSizing: 'border-box' as const,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#b0b0b0',
  marginBottom: '6px',
  fontSize: '13px',
};

const RELATION_OPTIONS = [
  { value: 'partner', label: 'Романтический партнёр' },
  { value: 'family', label: 'Семья / Родственник' },
  { value: 'friend', label: 'Друг / Подруга' },
  { value: 'colleague', label: 'Коллега / Партнёр по бизнесу' },
  { value: 'other', label: 'Другое' },
];

// ============================================================
// Компонент
// ============================================================

const PartnerForm: React.FC<PartnerFormProps> = ({ onSubmit, onCancel }) => {
  const [form, setForm] = useState<PartnerFormData>({
    name: '',
    relation: 'partner',
    birthDate: '',
    birthTime: '',
    federalDistrict: '',
    subject: '',
    district: '',
    settlement: '',
    latitude: '',
    longitude: '',
  });

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = <K extends keyof PartnerFormData>(field: K, value: PartnerFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Автоподстановка координат при выборе населённого пункта
  useEffect(() => {
    if (form.federalDistrict && form.subject && form.district && form.settlement) {
      const settlements = getSettlements(form.federalDistrict, form.subject, form.district);
      const selected = settlements.find(s => s.name === form.settlement);
      if (selected) {
        setForm(prev => ({
          ...prev,
          latitude: selected.latitude.toString(),
          longitude: selected.longitude.toString(),
        }));
      }
    }
  }, [form.settlement, form.federalDistrict, form.subject, form.district]);

  const handleSubmit = async () => {
    setError(null);

    if (!form.name.trim()) {
      setError('Введите имя');
      return;
    }
    if (!form.birthDate) {
      setError('Выберите дату рождения');
      return;
    }
    if (!form.latitude || !form.longitude) {
      setError('Укажите место рождения (выберите населённый пункт)');
      return;
    }

    const lat = parseFloat(form.latitude);
    const lon = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lon)) {
      setError('Координаты должны быть числами');
      return;
    }

    const birthPlace = form.settlement
      ? `${form.settlement}, ${form.district}, ${form.subject}`
      : 'Неизвестно';

    setSubmitting(true);

    try {
      const res = await api.post<any>('/synastry/contacts', {
        name: form.name.trim(),
        relation: form.relation,
        birthDate: form.birthDate,
        birthTime: form.birthTime || undefined,
        birthPlace,
        latitude: lat,
        longitude: lon,
        timezone: 'UTC',
      });

      if (res.success && res.data) {
        onSubmit({
          name: form.name.trim(),
          relation: form.relation,
          birthDate: form.birthDate,
          birthTime: form.birthTime || undefined,
          birthPlace,
          latitude: lat,
          longitude: lon,
        });
      } else {
        setError(res.message || res.error || 'Не удалось сохранить контакт');
      }
    } catch (err) {
      setError('Ошибка сети. Попробуйте снова.');
    } finally {
      setSubmitting(false);
    }
  };

  const settlementsList: Settlement[] = getSettlements(
    form.federalDistrict,
    form.subject,
    form.district
  );

  return (
    <div style={{ ...cardStyle, width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      <h2
        style={{
          fontSize: '22px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          marginBottom: '24px',
          marginTop: 0,
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}
      >
        Новый контакт
      </h2>

      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.15)',
          border: '1px solid rgba(220, 53, 69, 0.3)',
          color: '#dc3545',
          padding: '10px 14px',
          borderRadius: '10px',
          fontSize: '14px',
          marginBottom: '8px',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {/* Имя */}
        <div>
          <label style={labelStyle}>Имя *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Имя человека"
            style={inputStyle}
          />
        </div>

        {/* Тип отношений */}
        <div>
          <label style={labelStyle}>Тип отношений</label>
          <select
            value={form.relation}
            onChange={(e) => updateField('relation', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {RELATION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} style={{ background: '#1a1a3e' }}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Дата рождения */}
        <div>
          <label style={labelStyle}>Дата рождения *</label>
          <input
            type="date"
            value={form.birthDate}
            onChange={(e) => updateField('birthDate', e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Время рождения */}
        <div>
          <label style={labelStyle}>Время рождения (необязательно)</label>
          <input
            type="time"
            value={form.birthTime}
            onChange={(e) => updateField('birthTime', e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Регион: Округ + Субъект */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Федеральный округ</label>
            <select
              value={form.federalDistrict}
              onChange={(e) => {
                updateField('federalDistrict', e.target.value);
                updateField('subject', '');
                updateField('district', '');
                updateField('settlement', '');
              }}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Выберите округ</option>
              {RUSSIA_REGIONS.map((fd) => (
                <option key={fd.name} value={fd.name} style={{ background: '#1a1a3e' }}>{fd.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Субъект</label>
            <select
              value={form.subject}
              onChange={(e) => {
                updateField('subject', e.target.value);
                updateField('district', '');
                updateField('settlement', '');
              }}
              disabled={!form.federalDistrict}
              style={{ ...inputStyle, cursor: form.federalDistrict ? 'pointer' : 'not-allowed' }}
            >
              <option value="">Выберите субъект</option>
              {form.federalDistrict && getSubjects(form.federalDistrict).map((s) => (
                <option key={s.name} value={s.name} style={{ background: '#1a1a3e' }}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Район + Населённый пункт */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Район</label>
            <select
              value={form.district}
              onChange={(e) => {
                updateField('district', e.target.value);
                updateField('settlement', '');
              }}
              disabled={!form.subject}
              style={{ ...inputStyle, cursor: form.subject ? 'pointer' : 'not-allowed' }}
            >
              <option value="">Выберите район</option>
              {form.federalDistrict && form.subject && getDistricts(form.federalDistrict, form.subject).map((d) => (
                <option key={d.name} value={d.name} style={{ background: '#1a1a3e' }}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Населённый пункт</label>
            <select
              value={form.settlement}
              onChange={(e) => updateField('settlement', e.target.value)}
              disabled={!form.district}
              style={{ ...inputStyle, cursor: form.district ? 'pointer' : 'not-allowed' }}
            >
              <option value="">Выберите населённый пункт</option>
              {form.federalDistrict && form.subject && form.district && settlementsList.map((s) => (
                <option key={s.name} value={s.name} style={{ background: '#1a1a3e' }}>
                  {s.name} ({s.type === 'city' ? 'город' : s.type === 'town' ? 'посёлок' : s.type === 'village' ? 'деревня' : 'село'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Координаты (авто) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Широта (авто)</label>
            <input
              type="number"
              step="0.0001"
              value={form.latitude}
              readOnly
              placeholder="Выбери город"
              style={{ ...inputStyle, opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Долгота (авто)</label>
            <input
              type="number"
              step="0.0001"
              value={form.longitude}
              readOnly
              placeholder="Выбери город"
              style={{ ...inputStyle, opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>
        </div>
      </div>

      {/* Кнопки */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          style={{ ...btnPrimaryStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'wait' : 'pointer' }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Сохранение...' : 'Сохранить'}
        </button>
        <button style={btnSecondaryStyle} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </div>
  );
};

export default PartnerForm;
